import type { BasketLastItem, BasketResponseItem } from "@onestore/api/basket"
import type {
  BasketPatchItem,
  BasketPatchResource,
  JSONData,
  Price,
  ProductAlias,
  TaxRate,
} from "@onestore/api/types"
import type { HTTP } from "@onestore/onestore-store-common"
import { itemsBasketPrice } from "@onestore/onestore-store-common/api/operations"
import {
  CloudBlueProductsResponseSchema,
  type CloudBlueProductType,
} from "@onestore/onestore-store-common/api/shapes"
import { captureErrorMessage } from "@onestore/onestore-store-common/debug"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import { normalizeMaxQuantity } from "@gatsby-plugin-bonus/lib/normalizers"
import {
  getParsedQuery,
  type QueryConfigData,
} from "@gatsby-plugin-bonus/lib/query-parser"
import type { BonusPeriod, BonusResource } from "@gatsby-plugin-bonus/types"
import { isBonusProductWithRemoteStateType } from "@gatsby-plugin-bonus/types"
import { fetchDedupe } from "fetch-dedupe"
import queryString from "query-string"
import api, { apiUrl } from "~/lib/api"
import { build } from "~/lib/config"
import SessionStorage from "~/lib/storage/SessionStorage"
import { updateItemsInBasket } from "../basket/actions"
import { BasketActionSource, BasketActions } from "../basket/constants"
import type { AppDispatch, AppState } from "../reducer"
import type { BonusBasketResource } from "./actions-bonus"
import {
  addBonusItemToBasket,
  changeAdvancedBonusItemResourcesGroup,
  changeBonusItemInBasket,
  changeBonusItemPrice,
  changeBonusItemResource,
  getProductBasketWithResources,
} from "./actions-bonus"
import {
  getBonusPromoCode,
  getProduct,
  isBonusParameterFormValid,
} from "./selectors"
import type { BonusProduct, BonusResources } from "./types"
import { waitForBonusItemIdle } from "./waitForBonusItemIdle"

export enum BonusProductActionsType {
  BONUS_PARAMETERS_FORM_UPDATE = "@bonus/BONUS_FORM_UPDATE",
  BONUS_CHANGE_PRODUCT_BASKET_STATE = "@bonus/CHANGE_PRODUCT_BASKET_STATE",
  BONUS_CHANGE_PRODUCT_PERIOD_REQUEST = "@bonus/CHANGE_PRODUCT_PERIOD_REQUEST",
  BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS = "@bonus/CHANGE_PRODUCT_PERIOD_SUCCESS",
  BONUS_CHANGE_PRODUCT_PERIOD_FAILURE = "@bonus/CHANGE_PRODUCT_PERIOD_FAILURE",

  BONUS_UPDATE_PRODUCT_PRICE_REQUEST = "@bonus/UPDATE_PRODUCT_PRICE_REQUEST",
  BONUS_UPDATE_PRODUCT_PRICE_SUCCESS = "@bonus/UPDATE_PRODUCT_PRICE_SUCCESS",
  BONUS_UPDATE_PRODUCT_PRICE_FAILURE = "@bonus/UPDATE_PRODUCT_PRICE_FAILURE",

  BONUS_PRODUCT_ADD_TO_BASKET_REQUEST = "@bonus/PRODUCT_ADD_TO_BASKET_REQUEST",
  BONUS_PRODUCT_ADD_TO_BASKET_SUCCESS = "@bonus/PRODUCT_ADD_TO_BASKET_SUCCESS",
  BONUS_PRODUCT_ADD_TO_BASKET_FAILURE = "@bonus/PRODUCT_ADD_TO_BASKET_FAILURE",

  BONUS_PRODUCT_CHANGE_BASKET_REQUEST = "@bonus/PRODUCT_CHANGE_BASKET_REQUEST",
  BONUS_PRODUCT_CHANGE_BASKET_SUCCESS = "@bonus/PRODUCT_CHANGE_BASKET_SUCCESS",
  BONUS_PRODUCT_CHANGE_BASKET_FAILURE = "@bonus/PRODUCT_CHANGE_BASKET_FAILURE",

  BONUS_CHANGE_PRODUCT_RESOURCE_REQUEST = "@bonus/CHANGE_PRODUCT_RESOURCE_REQUEST",
  BONUS_CHANGE_PRODUCT_RESOURCE_SUCCESS = "@bonus/CHANGE_PRODUCT_RESOURCE_SUCCESS",
  BONUS_CHANGE_PRODUCT_RESOURCE_FAILURE = "@bonus/CHANGE_PRODUCT_RESOURCE_FAILURE",

  BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_REQUEST = "@bonus/PRODUCT_CHANGE_ADVANCED_RESOURCE_REQUEST",
  BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_SUCCESS = "@bonus/PRODUCT_CHANGE_ADVANCED_RESOURCE_SUCCESS",
  BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_FAILURE = "@bonus/PRODUCT_CHANGE_ADVANCED_RESOURCE_FAILURE",

  BONUS_GET_PRODUCT_PRICE_REQUEST = "@bonus/GET_PRODUCT_PRICE_REQUEST",
  BONUS_GET_PRODUCT_PRICE_SUCCESS = "@bonus/GET_PRODUCT_PRICE_SUCCESS",
  BONUS_GET_PRODUCT_PRICE_FAILURE = "@bonus/GET_PRODUCT_PRICE_FAILURE",

  BONUS_CHANGE_PRODUCT_PRICE = "@bonus/CHANGE_PRODUCT_PRICE",

  BONUS_VALIDATE_PRODUCT_REQUEST = "@bonus/VALIDATE_PRODUCT_REQUEST",
  BONUS_VALIDATE_PRODUCT_SUCCESS = "@bonus/VALIDATE_PRODUCT_SUCCESS",
  BONUS_VALIDATE_PRODUCT_FAILURE = "@bonus/VALIDATE_PRODUCT_FAILURE",

  BONUS_PRODUCT_NEEDS_UPDATE = "@bonus/PRODUCT_NEEDS_UPDATE",
  BONUS_CHANGE_PERIOD_CLICK_REQUEST = "@bonus/CHANGE_PERIOD_CLICK_REQUEST",
  BONUS_CHANGE_PERIOD_CLICK = "@bonus/CHANGE_PERIOD_CLICK",
}

export const PRODUCT_DEFINITIONS_GET_REQUEST =
  "@definitions/PRODUCT_DEFINITIONS_GET_REQUEST"
export const PRODUCT_DEFINITIONS_GET_SUCCESS =
  "@definitions/PRODUCT_DEFINITIONS_GET_SUCCESS"
export const PRODUCT_DEFINITIONS_GET_FAILURE =
  "@definitions/PRODUCT_DEFINITIONS_GET_FAILURE"
export const BASKET_UPSELL_DEFINITION_GET_SUCCESS =
  "@definitions/BASKET_UPSELL_DEFINITION_GET_SUCCESS"

type BonusChangeProductBasketStateAction = {
  basketItem: BasketResponseItem
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_BASKET_STATE
}
export type BonusChangeProductPeriodRequestAction = {
  status: HTTP.Status
  period: BonusPeriod
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_REQUEST
}

export type BonusChangeProductPeriodSuccessAction = {
  status: number
  promoPrice: Price | null
  regularPrice: Price
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS
  lastItems?: BasketLastItem[]
  basketItemId?: number
}

export type BonusChangeProductPeriodFailureAction = {
  error: string | undefined
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_FAILURE
}

type BonusUpdateProductPriceRequestAction = {
  type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_REQUEST
}

type BonusUpdateProductPriceSuccessAction = {
  totalPrices: {
    regularPrice: Price
    promoPrice: Price
  }
  type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_SUCCESS
}

type BonusUpdateProductPriceFailureAction = {
  type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_FAILURE
}

type BonusProductAddToBasketRequestAction = {
  status: HTTP.Status
  automaticAdd: boolean
  type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_REQUEST
}

type BonusProductAddToBasketSuccessAction = {
  resources: BonusResources
  basketItemId: number
  lastItems: BasketLastItem[]
  status: HTTP.Status
  chosenPeriodId: number
  chosenPeriodName: string
  basketItemTaxRate: TaxRate
  basketItemPrice: number
  type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_SUCCESS
}

type BonusProductAddToBasketFailureAction = {
  status: HTTP.Status
  error: JSONData
  type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_FAILURE
}

type BonusProductChangeBasketRequestAction = {
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_REQUEST
  status: HTTP.Status
}

type BonusProductChangeBasketSuccessAction = {
  [x: string]: any
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_SUCCESS
  lastItems: BasketLastItem[]
}

type BonusProductChangeBasketFailureAction = {
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_FAILURE
}

export type BonusChangeProductResourceRequestAction = {
  resource: BonusResource
  quantity: number
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_REQUEST
}

type BonusChangeProductResourceSuccessAction = {
  resources: BonusResources
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_SUCCESS
}

type BonusChangeProductResourceFailureAction = {
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_FAILURE
}

type BonusProductChangeAdvancedResourceRequestAction = {
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_REQUEST
}

export type BonusProductChangeAdvancedResourceSuccessAction = {
  resources: BonusBasketResource[]
  promoPrice: Price | null
  regularPrice: Price
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_SUCCESS
}

type BonusProductChangeAdvancedResourceFailureAction = {
  type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_FAILURE
}

type BonusGetProductPriceRequestAction = {
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_REQUEST
}

type BonusGetProductPriceSuccessAction = {
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_SUCCESS
}

type BonusGetProductPriceFailureAction = {
  error: string | undefined
  status: HTTP.Status
  type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_FAILURE
}

export type BonusChangeProductPriceAction = {
  periods: BonusPeriod[]
  type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PRICE
}

type BonusValidateProductRequestAction = {
  type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_REQUEST
}

type BonusValidateProductSuccessAction = {
  errors: Record<string, string>
  isValid: boolean
  type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_SUCCESS
}

type BonusValidateProductFailureAction = {
  errors: Record<string, string>
  isValid: boolean
  type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_FAILURE
}

type BonusProductNeedsUpdateAction = {
  type: BonusProductActionsType.BONUS_PRODUCT_NEEDS_UPDATE
}

type BonusChangeProductPeriodClickRequestAction = {
  type: BonusProductActionsType.BONUS_CHANGE_PERIOD_CLICK_REQUEST
}

type BonusChangeProductPeriodClickAction = {
  status: number
  promoPrice: Price | null
  regularPrice: Price
  period: BonusPeriod
  type: BonusProductActionsType.BONUS_CHANGE_PERIOD_CLICK
  lastItems?: BasketLastItem[]
  basketItemId?: number
}

export type BonusProductActions =
  | BonusChangeProductBasketStateAction
  | BonusChangeProductPeriodRequestAction
  | BonusChangeProductPeriodSuccessAction
  | BonusChangeProductPeriodFailureAction
  | BonusUpdateProductPriceRequestAction
  | BonusUpdateProductPriceSuccessAction
  | BonusUpdateProductPriceFailureAction
  | BonusProductAddToBasketRequestAction
  | BonusProductAddToBasketSuccessAction
  | BonusProductAddToBasketFailureAction
  | BonusProductChangeBasketRequestAction
  | BonusProductChangeBasketSuccessAction
  | BonusProductChangeBasketFailureAction
  | BonusChangeProductResourceRequestAction
  | BonusChangeProductResourceSuccessAction
  | BonusChangeProductResourceFailureAction
  | BonusProductChangeAdvancedResourceRequestAction
  | BonusProductChangeAdvancedResourceSuccessAction
  | BonusProductChangeAdvancedResourceFailureAction
  | BonusGetProductPriceRequestAction
  | BonusGetProductPriceSuccessAction
  | BonusGetProductPriceFailureAction
  | BonusChangeProductPriceAction
  | BonusValidateProductRequestAction
  | BonusValidateProductSuccessAction
  | BonusValidateProductFailureAction
  | BonusProductNeedsUpdateAction
  | BonusChangeProductPeriodClickRequestAction
  | BonusChangeProductPeriodClickAction

export type ProductDefinitionsGetRequestAction = {
  type: "@definitions/PRODUCT_DEFINITIONS_GET_REQUEST"
}

export type ProductDefinitionsGetSuccessAction = {
  definitions: CloudBlueProductType[]
  config: QueryConfigData
  status: HTTP.Status
  type: "@definitions/PRODUCT_DEFINITIONS_GET_SUCCESS"
}

export type ProductDefinitionsGetFailureAction = {
  type: "@definitions/PRODUCT_DEFINITIONS_GET_FAILURE"
  status: HTTP.Status
}

export type ProductDefinitionActions =
  | ProductDefinitionsGetRequestAction
  | ProductDefinitionsGetSuccessAction
  | ProductDefinitionsGetFailureAction

export type BasketUpsellDefinitionGetSuccessAction = {
  type: typeof BASKET_UPSELL_DEFINITION_GET_SUCCESS
  alias: ProductAlias
  definitions: CloudBlueProductType[]
}

export function requestBonusProductsDefinitions() {
  return async (dispatch: AppDispatch, _getState: { (): AppState }) => {
    dispatch(requestProductDefinitionsByQuery())
  }
}

export function fetchDefinitionsByAliases(
  aliases: (string | undefined | null)[]
): Promise<CloudBlueProductType[]> {
  const buildKey = `def-${build || ""}-x${aliases.join("-")}`
  const cachedDefinition = SessionStorage.get(buildKey)

  if (cachedDefinition) {
    try {
      return JSON.parse(cachedDefinition)
    } catch {
      // ignore
    }
  }

  return fetchDedupe(
    `${apiUrl}/products/aliases?${queryString.stringify({ filter: aliases }, { arrayFormat: "index" })}`,
    {
      method: "GET",
    }
  )
    .then((res) => res.data)
    .then((responseData) => {
      const { data, success, error } =
        CloudBlueProductsResponseSchema.safeParse(responseData)

      if (success) {
        SessionStorage.set(buildKey, JSON.stringify(data))

        return data
      } else {
        captureErrorMessage(`API Validation Error`, {
          dto: responseData,
          error: error.message,
          issues: error.issues,
        })

        return data
      }
    })
}

function fetchDefinitionsWithCache(
  config: QueryConfigData
): Promise<CloudBlueProductType[]> {
  const aliases = [
    config.product?.alias,
    ...(config.upsell?.map((item) => item.alias) || []),
    config.bundled?.alias,
    ...(config.update?.map((item) => item.alias) || []),
  ]

  return fetchDefinitionsByAliases(aliases)
}

export function requestProductDefinitionsByQuery() {
  return async (dispatch) => {
    dispatch({
      type: PRODUCT_DEFINITIONS_GET_REQUEST,
      status: HTTP_STATUS.CONTINUE,
    } as ProductDefinitionsGetRequestAction)

    try {
      const parsedData = getParsedQuery()

      const definitions = await fetchDefinitionsWithCache(parsedData)

      dispatch({
        type: PRODUCT_DEFINITIONS_GET_SUCCESS,
        status: HTTP_STATUS.OK,
        definitions,
        config: parsedData,
      } as ProductDefinitionsGetSuccessAction)

      return Promise.resolve()
    } catch (error) {
      dispatch({
        type: PRODUCT_DEFINITIONS_GET_FAILURE,
        status: HTTP_STATUS.BAD_REQUEST,
      } as ProductDefinitionsGetFailureAction)

      return Promise.reject(error)
    }
  }
}

export function requestBasketUpsellDefinition(alias: ProductAlias) {
  return async (dispatch: AppDispatch) => {
    const definitions = await fetchDefinitionsWithCache({
      upsell: [{ alias }],
    } as QueryConfigData)

    dispatch({
      type: BASKET_UPSELL_DEFINITION_GET_SUCCESS,
      alias,
      definitions,
    } as BasketUpsellDefinitionGetSuccessAction)
  }
}

/**
 * Tworzy obiekt elementu koszyka do wysłania do api.
 *
 * @param {object} product
 */
function createProductItem(product: BonusProduct): BasketPatchItem[] {
  return product.periods.map((period) => {
    const item: BasketPatchItem = {
      plan: product.planId,
      planPeriod: period.id,
      quantity: 1,
    }

    if (product.resources) {
      const itemResources: BasketPatchResource[] = []

      Object.values(product.resources)
        .filter((resource) => 0 < resource.basketQuantity)
        .forEach((resource) => {
          itemResources.push({
            id: resource.id,
            quantity: resource.basketQuantity,
          })
        })

      item.resources = itemResources
    }

    return item
  })
}

async function getProductItemPrice(product: BonusProduct) {
  const items = createProductItem(product)
  const promises = items.map((item) =>
    api.calculateBasket({ items: itemsBasketPrice([item]) })
  )
  const results = await Promise.all(promises)

  return Promise.resolve(results.map((result) => result.items[0]))
}

export function changeProductPrice(
  product: BonusProduct,
  items: BasketResponseItem[] | BasketResponseItem[][]
) {
  return (dispatch: AppDispatch): Promise<BonusProduct["periods"]> => {
    const periods = changeBonusItemPrice(product, items)

    dispatch({
      type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PRICE,
      periods,
    } as BonusChangeProductPriceAction)

    return Promise.resolve(periods)
  }
}

/**
 * Zmiana okresu głównego produktu w przed-koszyku.
 * @todo     wydzielić do osobnej funkcji zmianę okresu produktu/upsellu
 *
 * @param   {Product} product  ID elementu koszyka, któremu zmieniamy okres.
 * @param   {object}  period   Okres na jaki chcemy zmienić.
 * @returns {Promise.<object>} Zwraca zmodyfikowany element koszyka.
 */

export enum ActionType {
  PERIOD_CHANGE = "period_change",
}

export function requestChangeProductPeriod(
  product: BonusProduct,
  period,
  quantity: undefined | number = undefined,
  action?: ActionType,
  type?: BasketActions
) {
  return async (dispatch: AppDispatch) => {
    if (action === ActionType.PERIOD_CHANGE) {
      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PERIOD_CLICK_REQUEST,
      } as BonusChangeProductPeriodClickRequestAction)

      // Product not yet in basket (e.g. ax-drawer): update period locally without touching the basket API
      if (!isBonusProductWithRemoteStateType(product)) {
        dispatch({
          type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_REQUEST,
          status: HTTP_STATUS.CONTINUE,
          period,
        } as BonusChangeProductPeriodRequestAction)

        try {
          const totalPrices = await getProductBasketWithResources(
            product,
            period.id
          )

          return dispatch({
            type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS,
            status: HTTP_STATUS.OK,
            period,
            ...totalPrices,
          } as BonusChangeProductPeriodSuccessAction)
        } catch (error) {
          return dispatch({
            type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_FAILURE,
            status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
            error: error instanceof Error ? error.message : undefined,
          } as BonusChangeProductPeriodFailureAction)
        }
      }

      const totalPrices = await getProductBasketWithResources(
        product,
        period.id
      )
      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PERIOD_CLICK,
        status: HTTP_STATUS.OK,
        period,
        ...totalPrices,
      } as BonusChangeProductPeriodClickAction)
    }

    if (!isBonusProductWithRemoteStateType(product)) {
      return
    }

    dispatch({
      type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_REQUEST,
      status: HTTP_STATUS.CONTINUE,
      itemId: product.basketItemState.id,
      period,
    } as BonusChangeProductPeriodRequestAction)

    if (!product.addedToBasket) {
      const totalPrices = await getProductBasketWithResources(
        product,
        period.id
      )

      return dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS,
        status: HTTP_STATUS.OK,
        period,
        ...totalPrices,
      } as BonusChangeProductPeriodSuccessAction)
    }

    try {
      const changedItems: BasketLastItem[] = await dispatch(
        updateItemsInBasket(
          [
            {
              id: product.basketItemState.id,
              planPeriod: period.id,
              quantity,
            },
          ],
          BasketActionSource.BONUS,
          () => {},
          type || BasketActions.BASKET_CHANGE_TERM
        )
      )

      const { regularPrice, promoPrice } = await getProductBasketWithResources(
        product,
        period.id
      )

      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS,
        status: HTTP_STATUS.OK,
        period,
        regularPrice,
        promoPrice,
        lastItems: changedItems,
        basketItemId: product.basketItemState.id,
      } as BonusChangeProductPeriodSuccessAction)

      return Promise.resolve(changedItems[0])
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_FAILURE,
        status: HTTP_STATUS.BAD_REQUEST,
        error,
      } as BonusChangeProductPeriodFailureAction)

      return Promise.reject(error)
    }
  }
}

/**
 * Edytuje dany zasób w głównym produkcie.
 *
 * @param {object} product   Obiekt głównego produktu.
 * @param {object} resource Zasób, który jest edytowany.
 * @param {number} quantity Wartość, o którą ma się zwiekszyć/zmniejszyć ilość danego zasobu.
 */
export function requestChangeProductResource(
  resource: BonusResource,
  quantity: number
) {
  return async (
    dispatch: AppDispatch,
    getState: { (): AppState }
  ): Promise<void> => {
    const newQuantity = resource.quantity + quantity

    if (
      newQuantity > normalizeMaxQuantity(resource.maxQuantity) ||
      newQuantity < resource.minQuantity
    ) {
      return
    }

    await dispatch({
      type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_REQUEST,
      status: HTTP_STATUS.CONTINUE,
      resource,
      quantity,
    } as BonusChangeProductResourceRequestAction)

    const product = getProduct(getState())

    try {
      await dispatch(requestGetProductPrice())

      const currentResources = await changeBonusItemResource(
        dispatch,
        {
          addedToBasket: product?.addedToBasket || false,
          alias: product?.alias,
          basketItemState: product?.basketItemState,
          resources: product?.resources || {},
        },
        resource,
        newQuantity
      )

      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_SUCCESS,
        status: HTTP_STATUS.OK,
        product,
        resources: currentResources || {},
      } as BonusChangeProductResourceSuccessAction)
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_RESOURCE_FAILURE,
        product,
        quantity,
        error,
      } as BonusChangeProductResourceFailureAction)

      return Promise.reject(error)
    }
  }
}

/**
 * Akcja dodająca główny produkt przed-koszyka do koszyka użytkownika.
 *
 * @param   {string} promoCode  Kod promocyjny.
 * @returns {Promise.<object>} Zwraca dodany element koszyka.
 */
export function requestAddProductToBasket(
  promoCode?: string,
  automatic?: boolean
) {
  return async (
    dispatch: AppDispatch,
    getState: { (): AppState }
  ): Promise<BasketLastItem | undefined> => {
    dispatch({
      type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_REQUEST,
      status: HTTP_STATUS.CONTINUE,
      automaticAdd: automatic || false,
    } as BonusProductAddToBasketRequestAction)

    try {
      const result = await dispatch(requestValidateProduct())

      if (!result.isValid) {
        return Promise.resolve(undefined)
      }

      const product = getProduct(getState())

      if (!product) {
        return Promise.resolve(undefined)
      }

      const { addedItem, resources, lastItems } = await addBonusItemToBasket(
        dispatch,
        getState,
        product,
        null,
        promoCode
      )

      if ("plan_id" in addedItem) {
        dispatch({
          type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_SUCCESS,
          basketItemId: addedItem.id,
          lastItems,
          basketItemPrice: addedItem.price,
          basketItemTaxRate: addedItem.tax_rate,
          status: HTTP_STATUS.OK,
          chosenPeriodId: product.chosenPeriodId,
          chosenPeriodName: addedItem.period_name,
          resources,
        } as BonusProductAddToBasketSuccessAction)
      }

      return Promise.resolve(addedItem)
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_PRODUCT_ADD_TO_BASKET_FAILURE,
        status: error.code || HTTP_STATUS.BAD_REQUEST,
        error,
      } as BonusProductAddToBasketFailureAction)

      return Promise.resolve(undefined)
    }
  }
}

export function requestChangeProductInBasket(product: BonusProduct) {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (!isBonusProductWithRemoteStateType(product)) {
      return
    }

    // The CTA is briefly re-enabled between the caller's own loading flag
    // clearing and this validation await resolving (single microtask tick,
    // accepted as low impact rather than adding a second loading dispatch).
    const result = await dispatch(requestValidateProduct())

    if (!result.isValid) {
      return
    }

    dispatch({
      type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_REQUEST,
      status: HTTP_STATUS.CONTINUE,
      product,
    } as BonusProductChangeBasketRequestAction)

    try {
      const { chosenPeriodId, changedResources, lastItems } = await dispatch(
        changeBonusItemInBasket(product)
      )

      dispatch({
        type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_SUCCESS,
        status: HTTP_STATUS.OK,
        chosenPeriodId,
        changedResources,
        lastItems,
      } as BonusProductChangeBasketSuccessAction)
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_BASKET_FAILURE,
        status: error.code || HTTP_STATUS.BAD_REQUEST,
        error,
        product,
      } as BonusProductChangeBasketFailureAction)
    }
  }
}

/**
 * Pushes a changed parameter (e.g. the data center location) to the basket
 * item. Parameters live in the form, not in the resources, so
 * `changeAdvancedProductResourcesGroup` never sees them.
 */
export function syncProductParametersToBasket() {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    await waitForBonusItemIdle(() => getProduct(getState()))

    const product = getProduct(getState())

    if (!product?.addedToBasket || !product.advancedConfiguration) {
      return
    }

    if (!isBonusParameterFormValid(product.alias)(getState())) {
      return
    }

    await dispatch(requestChangeProductInBasket(product))
  }
}

export function changeAdvancedProductResourcesGroup(
  product: BonusProduct,
  resourcesList: number[],
  chosenResourceId: number,
  basketQuantity: number,
  promoCode: string | null | undefined = null
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({
      type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_REQUEST,
    } as BonusProductChangeAdvancedResourceRequestAction)

    try {
      const result = await dispatch(
        changeAdvancedBonusItemResourcesGroup(
          product,
          resourcesList,
          chosenResourceId,
          basketQuantity,
          promoCode
        )
      )

      if (result === null) {
        return
      }

      const {
        totalPrice: { regularPrice, promoPrice },
        resources,
      } = result

      dispatch({
        type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_SUCCESS,
        resources,
        regularPrice,
        promoPrice,
      } as BonusProductChangeAdvancedResourceSuccessAction)

      // Resource/parameter changes only recalculate the price locally — the
      // basket position itself is stale until this runs, so chain it here
      // instead of relying on the user to press a separate "update basket"
      // button. Re-read the product from the store (rather than reusing the
      // stale `product` argument) so the basket PATCH carries the
      // just-updated resource quantities from the SUCCESS dispatch above.
      const updatedProduct = getProduct(getState())

      if (updatedProduct?.addedToBasket) {
        await dispatch(requestChangeProductInBasket(updatedProduct))
      }
    } catch {
      dispatch({
        type: BonusProductActionsType.BONUS_PRODUCT_CHANGE_ADVANCED_RESOURCE_FAILURE,
      } as BonusProductChangeAdvancedResourceFailureAction)
    }
  }
}

export function requestGetProductPrice() {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    dispatch({
      type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_REQUEST,
      status: HTTP_STATUS.CONTINUE,
    } as BonusGetProductPriceRequestAction)

    const product = getProduct(getState())

    if (!product) {
      return Promise.reject("Missing product")
    }
    try {
      const items = await getProductItemPrice(product)
      const updates = await dispatch(changeProductPrice(product, items))

      dispatch({
        type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_SUCCESS,
        status: HTTP_STATUS.OK,
      } as BonusGetProductPriceSuccessAction)

      return Promise.resolve(updates)
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_GET_PRODUCT_PRICE_FAILURE,
        status: error.code,
        error,
      } as BonusGetProductPriceFailureAction)

      return Promise.reject(error)
    }
  }
}

export function requestValidateProduct() {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    const state = getState()
    const product = getProduct(state)

    if (!product) {
      return {
        isValid: true,
        errors: [],
      }
    }

    dispatch({
      type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_REQUEST,
      product,
    } as BonusValidateProductRequestAction)

    const isValid = isBonusParameterFormValid(product.alias)(state)

    if (isValid) {
      dispatch({
        type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_SUCCESS,
        isValid,
      } as BonusValidateProductSuccessAction)

      return {
        isValid,
      }
    }

    dispatch({
      type: BonusProductActionsType.BONUS_VALIDATE_PRODUCT_FAILURE,
      isValid,
    } as BonusValidateProductFailureAction)

    return {
      isValid,
    }
  }
}

export function productNeedsUpdate() {
  return (dispatch: AppDispatch) =>
    dispatch({
      type: BonusProductActionsType.BONUS_PRODUCT_NEEDS_UPDATE,
    } as BonusProductNeedsUpdateAction)
}

export function updateProductPrice() {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    dispatch({
      type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_REQUEST,
    } as BonusUpdateProductPriceRequestAction)
    try {
      const state = getState()
      const product = getProduct(state)

      if (!product) {
        dispatch({
          type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_FAILURE,
        } as BonusUpdateProductPriceFailureAction)

        return
      }
      const promoCode = getBonusPromoCode(state)

      const totalPrices = await getProductBasketWithResources(
        product,
        product?.chosenPeriodId,
        Object.values(product?.resources || {}),
        promoCode
      )

      dispatch({
        type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_SUCCESS,
        totalPrices,
      } as BonusUpdateProductPriceSuccessAction)
    } catch {
      dispatch({
        type: BonusProductActionsType.BONUS_UPDATE_PRODUCT_PRICE_FAILURE,
      } as BonusUpdateProductPriceFailureAction)
    }
  }
}
